const VueComponentWrappers = require('client/components/VueComponentWrappers');

/**
 * This class is a wrapper for the Modal Vue component. It replaces the legacy
 * Backbone Modal view by providing the same API as it but mapping the legacy
 * API to props on the Modal Vue component. This class is used so that legacy
 * Backbone views can use the Modal Vue component with out having to be modified
 * themselves to do so.
 *
 * WARNING: This class should not be used in Vue components! It is meant for use
 * in Backbone views to be a proxy to a Modal or ProgressModal component. Vue
 * components should use TheModal or a Modal component directly.
 */
module.exports = class Modal {
  constructor(settings = {}) {
    const defaultSettings = {
      button: { color: 'green', text: 'save' },
      cancelText: 'cancel',
      title: 'Modal Title',
      headerIcon: undefined,
      loading: false,
      content: 'Modal Content',
      disableActionButton: false,
      disableUserActions: false,
      showProgress: false,
      initialProgress: 0,
      maxProgress: 100,
      buttonEvent: () => this.hide(),
      cancelEvent: () => this.hide(),
    };

    this.data = {};
    this.settings = { ...defaultSettings, ...settings };
  }

  render() {
    const el = document.createElement('div');
    document.body.appendChild(el);

    this.convertSettingsToData();

    if (this.settings.showProgress) {
      this.modalWrapper = new VueComponentWrappers.ProgressModalWrapper(
        el,
        this.data,
      );
    } else {
      this.modalWrapper = new VueComponentWrappers.ModalWrapper(
        el,
        this.data,
        this.settings.content,
      );
    }

    this.modalWrapper.render();
    this.modalWrapper.on('updateVisibility', value => this.handleUpdateVisibility(value));
    this.modalWrapper.on('opened', () => this.handleOpened());
    this.modalWrapper.on('closed', () => this.handleClosed());

    this.show();

    // Return instance for consistency with render() method in legacy Backbone
    // Modal view.
    return this;
  }

  /**
   * The legacy Backbone Modal view emitted events as an alternative interface.
   * The supported events are: visible, cancel, button.
   */
  on(event, handler) {
    if (event === 'visible') {
      this.visibleHandler = handler;
    } else if (event === 'cancel') {
      this.cancelHandler = handler;
    } else if (event === 'button') {
      this.buttonHandler = handler;
    }

    // Return instance for consistency with jQuery's on() method used in legacy
    // Backone view.
    return this;
  }

  /**
   * This method maps the legacy Backbone Modal view's settings to the Vue Modal
   * component's props.
   */
  convertSettingsToData() {
    this.data = {
      cancelText: this.settings.cancelText,
      enableMainAction: !this.settings.disableActionButton,
      handleMainAction: () => this.handleMainAction(),
      headerText: this.settings.title,
      headerIcon: this.settings.headerIcon,
      height: this.settings.height,
      initialTotal: this.settings.showProgress ? this.settings.maxProgress : undefined,
      kbLink: this.settings.kbLink,
      loading: this.settings.loading,
      mainActionAttention: this.settings.button.color === 'red',
      mainActionText: this.settings.button.text,
      minHeight: this.settings.minHeight,
      minWidth: this.settings.minWidth,
      useStoreModule: this.settings.showProgress ? false : undefined,
      visible: false,
      width: this.settings.width,
    };
  }

  /**
   * Allow the caller to set props on the Modal component programmatically after
   * it has been rendered. Note that only the existing use cases in Backbone
   * views are supported in this method.
   */
  set(key, value) {
    switch (key) {
      case 'disableActionButton':
        this.data.enableMainAction = !value;
        break;

      default:
        this.data[key] = value;
        break;
    }
  }

  animateProgress(completed) {
    this.modalWrapper.updateProgressModal(completed, this.data.initialTotal);
  }

  handleMainAction() {
    if (this.settings.disableUserActions) {
      return;
    }

    if (this.buttonHandler) {
      this.buttonHandler();
    }

    this.settings.buttonEvent();
  }

  handleUpdateVisibility(value) {
    if (this.settings.disableUserActions) {
      return;
    }

    if (value) {
      this.show();
    } else {
      if (this.cancelHandler) {
        this.cancelHandler();
      }

      this.settings.cancelEvent();
    }
  }

  handleOpened() {
    if (this.visibleHandler) {
      this.visibleHandler();
    }
  }

  /**
   * The Modal component will emit the closed event once it has been transitioned
   * out and is no longer visible. So after cleaning up the wrapper the promise
   * returned in hide() will be resolved so that the caller can perform any
   * necessary actions after the modal is hidden.
   */
  handleClosed() {
    this.modalWrapper.cleanUp();
    this.modalWrapper = null;
    this.resolveOnHidden();
  }

  show() {
    this.data.visible = true;
  }

  /**
   * The caller will call this method to hide the modal. It will set the visible
   * property to false, which actually hides the Modal component, and then return
   * a promise. The promise will be resolved when the Modal component is
   * transitioned out.
   */
  hide() {
    if (this.settings.showProgress) {
      this.modalWrapper.hideProgressModal();
    } else {
      this.data.visible = false;
    }

    return new Promise((resolve) => { this.resolveOnHidden = resolve; });
  }
};
